import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Subject, debounceTime, filter, take } from 'rxjs';

import {
  CmsContentService,
  Materials,
} from '@digital-platform/rs-cms/services';
import { AbandonedCartModalComponent } from '../abandoned-cart-modal/abandoned-cart-modal.component';
import { AbandonCartGuard } from '../guards/abandon-cart-guard.service';
import { CartItem } from './cart-item.model';
import { Cart } from './cart.model';
import {
  Product,
  ProductSku,
  ProductDetail,
  ProductDetailValue,
  ProductSkuDetails,
  CartExclusionsKey,
} from './product.model';
import { emptySimpleCart } from './shop-cart.constants';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CartService {
  cookieService = inject(CookieService);
  httpClient = inject(HttpClient);
  modalService = inject(NgbModal);
  cmsService = inject(CmsContentService);
  router = inject(Router);
  abandonCartGuard = inject(AbandonCartGuard);

  public updateCart: Subject<void>;
  public clearCartEmitter: Subject<void>;
  public overrideCartWithValue: Subject<Cart>;
  private currentCart: BehaviorSubject<Cart>;
  public cartLoaded: Subject<Cart>;
  public currentCartCount: BehaviorSubject<number>;
  public showCartIcon = false;
  public returnedToWebsite = true;

  constructor() {
    this.cartLoaded = new BehaviorSubject<Cart>(emptySimpleCart);
    this.updateCart = new Subject<void>();
    this.clearCartEmitter = new Subject<void>();
    this.overrideCartWithValue = new Subject<Cart>();
    this.currentCart = new BehaviorSubject(emptySimpleCart);
    this.currentCartCount = new BehaviorSubject(0);

    this.currentCart.subscribe(() => {
      this.updateCart.next();
    });

    this.overrideCartWithValue
      .pipe(debounceTime(1000))
      .subscribe((cart: Cart) => {
        this.overrideCart(cart);
      });

    this.abandonCartGuard.setAllowRouteException(true);
    this.subscribeToRoute();
  }

  public subscribeToRoute(): void {
    if (
      this.currentCart?.value?.items?.length &&
      this.router.url.startsWith('/shop') &&
      !this.abandonCartGuard.allowRouteException &&
      !this.modalService.hasOpenModals()
    ) {
      this.abandonCartGuard.setDeactivate(false);
    } else {
      this.abandonCartGuard.setAllowRouteException(false);
      this.abandonCartGuard.setDeactivate(true);
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof (NavigationStart || NavigationEnd)
        )
      )
      .subscribe(($event: NavigationStart | NavigationEnd) => {
        if ($event instanceof NavigationStart) {
          if (
            this.currentCart?.value?.items?.length &&
            this.router.url.startsWith('/shop') &&
            !$event.url.startsWith('/shop') &&
            !this.abandonCartGuard.allowRouteException &&
            !this.modalService.hasOpenModals()
          ) {
            this.openCartModal(this.currentCart.value, false, $event.url);
            this.abandonCartGuard.setDeactivate(false);
          } else {
            this.abandonCartGuard.setAllowRouteException(false);
            this.abandonCartGuard.setDeactivate(true);
          }
        }

        if ($event instanceof NavigationEnd) {
          this.abandonCartGuard.setAllowRouteException(false);
        }
      });
  }

  public overrideCart(cart: Cart): void {
    this.currentCart.next(cart);
    this.cartLoaded.next(cart);
    this.calculateCartCount();
  }

  public calculateCartCount(): number | null {
    let cartAmount = !this.currentCart.value.cartReset
      ? this.currentCart.value.items.filter((item: CartItem): boolean =>
          this.byNotExcluded(item)
        ).length
      : null;

    if (cartAmount) {
      for (const item of this.currentCart.value.items) {
        if (item.quantity && item.quantity > 1) {
          cartAmount = cartAmount - 1 + item.quantity;
        }
      }
    }
    this.currentCartCount.next(cartAmount || 0);

    if (this.returnedToWebsite && !this.router.url.startsWith('/shop')) {
      this.returnedToWebsite = false;
      if ((this.calculateCartCount() || 0) > 0) {
        this.openCartModal(this.currentCart.value, true);
      }
    }

    this.showCartIcon = (cartAmount || 0) > 0;
    return cartAmount;
  }

  // Returning true means it will be excluded from the cart
  public byNotExcluded(item: CartItem): boolean {
    // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
    if (item.product) {
      return !this.getDetail(
        item.product,
        CartExclusionsKey['EXCLUDE_FROM_CART_QUANTITY']
      );
    } else {
      return true;
    }
  }

  public getDetail(
    item: Product | ProductSku | CartItem,
    key: CartExclusionsKey
  ): ProductDetailValue | undefined {
    let detailValue;
    const detail: ProductDetail<ProductDetailValue> | undefined =
      item.details?.[key];

    if (detail && detail.value && detail.value !== 'N/A') {
      detailValue = detail.value;
    }

    return detailValue;
  }

  openCartModal(
    cart: Cart = this.currentCart.value,
    returningUser = false,
    url = ''
  ) {
    const cartQuantity = this.calculateCartCount() || 0;

    cart.items = cart.items.filter((item: CartItem): boolean =>
      this.byNotExcluded(item)
    );

    cart = this.loadImages(cart);

    let modalTitle = '';

    if (returningUser) {
      modalTitle = `Welcome Back.</br>You have ${
        cartQuantity > 1 ? 'items' : 'an item'
      } in your cart.`;
    } else {
      modalTitle = `Don't forget the item${
        cartQuantity > 1 ? 's' : ''
      } in your cart.`;
    }

    const modalOptions: NgbModalOptions = {
      size: 'lg',
      centered: true,
    };

    const modalRef = this.modalService.open(
      AbandonedCartModalComponent,
      modalOptions
    );

    modalRef.componentInstance.title = modalTitle;
    modalRef.componentInstance.cart = cart;

    modalRef.componentInstance.cartAction
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.beforeProceedNavigation();
        this.overrideCart(emptySimpleCart);
        this.clearCartEmitter.next();
      });

    modalRef.result.then(
      (result) => {
        this.abandonCartGuard.setAllowRouteException(true);
        this.router.navigate(['/shop/checkout']);
      },
      (reason) => {
        this.beforeProceedNavigation();
        this.router.navigateByUrl(url);
      }
    );
  }

  beforeProceedNavigation(): void {
    this.abandonCartGuard.setDeactivate(true);
    this.abandonCartGuard.setAllowRouteException(true);
  }

  loadImages(cart: Cart): Cart {
    cart.items.forEach((item: CartItem) => {
      const productCategoryCode = item?.product?.categoryCode;

      if (productCategoryCode) {
        this.cmsService
          .getShopProductImage(productCategoryCode)
          .pipe(take(1))
          .subscribe((res: Materials) => {
            const code = item?.product?.code;

            if (code) {
              item.imageUrl = res?.products?.[code]?.media?.[0]?.url;
            } else {
              item.imageUrl = res?.media?.[0]?.url;
            }
          });
      }
    });

    return cart;
  }
}
